import { useEffect, useState } from 'react'
import { billingApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import type { BillingStatus } from '@/types'
import { CheckCircle, AlertCircle, Clock, ExternalLink, Users, TrendingUp } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

const STARTER_FEATURES = [
  'Chart of Accounts (4 templates)',
  'Manual bank import + CSV wizard',
  'Bank reconciliation (smart matching, rules)',
  'Journals & double-entry ledger',
  'Financial periods (lock & unlock)',
  'P&L, Balance Sheet, Trial Balance, Cash Summary',
  'PDF report export',
  'Document storage (per client)',
  'Client viewer portal (read-only access)',
  'Staff management & audit log',
  'Firm dashboard & aggregate reports',
]

const PREMIUM_FEATURES = [
  'Everything in Starter',
  'Invoicing & payment tracking',
  'Bills & accounts payable',
  'Tracking categories',
  'Budgeting & variance reports',
  'Fixed asset register & depreciation',
  'Priority support',
]

const STATUS_MAP: Record<string, { label: string; variant: 'info' | 'success' | 'warning' | 'destructive' | 'default' }> = {
  trialing: { label: 'Free Trial', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Past Due', variant: 'warning' },
  canceled: { label: 'Canceled', variant: 'destructive' },
  none: { label: 'No Subscription', variant: 'default' },
}

export default function BillingPage() {
  const { currentFirm } = useAuth()
  const [billing, setBilling] = useState<BillingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState(false)
  const [openingPortal, setOpeningPortal] = useState(false)

  useEffect(() => {
    if (!currentFirm) return
    billingApi.status(currentFirm.id)
      .then((res) => setBilling(res.data))
      .catch(() => toast.error('Failed to load billing info'))
      .finally(() => setLoading(false))
  }, [currentFirm])

  async function handleSubscribe() {
    if (!currentFirm) return
    setSubscribing(true)
    try {
      const res = await billingApi.checkout(currentFirm.id)
      window.location.href = res.data.url
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to start checkout'
      toast.error(msg)
    } finally {
      setSubscribing(false)
    }
  }

  async function handlePortal() {
    if (!currentFirm) return
    setOpeningPortal(true)
    try {
      const res = await billingApi.portal(currentFirm.id)
      window.open(res.data.url, '_blank')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to open billing portal'
      toast.error(msg)
    } finally {
      setOpeningPortal(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl space-y-5">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-2 gap-5">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  const totalClients = (billing?.starter_client_count ?? 0) + (billing?.premium_client_count ?? 0)
  const isSubscribed = billing?.status === 'active' || billing?.status === 'past_due'
  const trialNearEnd = billing?.is_on_trial && (billing?.trial_days_remaining ?? 0) <= 7
  const trialLimitApproaching = billing?.is_on_trial && totalClients >= (billing?.trial_client_limit ?? 5) - 1 && totalClients < (billing?.trial_client_limit ?? 5)

  const statusInfo = STATUS_MAP[billing?.status ?? 'none'] ?? STATUS_MAP.none

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Billing &amp; Subscription</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">{currentFirm?.name}</p>
      </div>

      {/* Trial warning banner */}
      {billing?.is_on_trial && trialNearEnd && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-medium text-amber-800">
              Your trial ends in {billing.trial_days_remaining} day{billing.trial_days_remaining !== 1 ? 's' : ''}
            </p>
            <p className="text-[13px] text-amber-600 mt-0.5">
              Subscribe now to keep all your clients and data — no disruption.
            </p>
          </div>
        </div>
      )}

      {trialLimitApproaching && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-medium text-amber-800">
              Almost at your trial client limit ({totalClients} of {billing?.trial_client_limit} used)
            </p>
            <p className="text-[13px] text-amber-600 mt-0.5">
              Subscribe now to add unlimited clients with no interruption.
            </p>
          </div>
        </div>
      )}

      {billing?.status === 'none' && !billing.is_on_trial && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-medium text-red-800">Your free trial has ended</p>
            <p className="text-[13px] text-red-600 mt-0.5">
              Subscribe to continue adding clients. Existing data is safe.
            </p>
          </div>
        </div>
      )}

      {/* Current status card */}
      {billing && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                  {billing.is_on_trial && (
                    <span className="flex items-center gap-1 text-[13px] text-[rgba(0,10,30,0.75)]">
                      <Clock className="w-3.5 h-3.5" />
                      {billing.trial_days_remaining} days remaining
                    </span>
                  )}
                </div>
                {billing.current_period_end && (
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">
                    Next billing date: {new Date(billing.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
                {billing.trial_end && billing.is_on_trial && (
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">
                    Trial ends: {new Date(billing.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
              {isSubscribed && billing.stripe_customer_id && (
                <Button variant="outline" size="sm" onClick={handlePortal} disabled={openingPortal}>
                  <ExternalLink className="w-3.5 h-3.5" />
                  {openingPortal ? 'Opening...' : 'Manage Subscription'}
                </Button>
              )}
            </div>

            {/* Client breakdown */}
            <Separator className="mb-4" />
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Users className="w-3.5 h-3.5 text-[rgba(0,10,30,0.75)]" />
                  <span className="text-[13px] text-[rgba(0,10,30,0.75)]">Starter clients</span>
                </div>
                <p className="text-2xl font-bold text-[#000A1E]">{billing.starter_client_count}</p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
                  {isSubscribed ? `$${(billing.starter_client_count * 5).toFixed(2)}/mo` : '$5/client/mo'}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Users className="w-3.5 h-3.5 text-[rgba(0,10,30,0.75)]" />
                  <span className="text-[13px] text-[rgba(0,10,30,0.75)]">Premium clients</span>
                </div>
                <p className="text-2xl font-bold text-[#000A1E]">{billing.premium_client_count}</p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
                  {isSubscribed ? `$${(billing.premium_client_count * 10).toFixed(2)}/mo` : '$10/client/mo'}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <TrendingUp className="w-3.5 h-3.5 text-[#0078C8]" />
                  <span className="text-[13px] text-[rgba(0,10,30,0.75)]">Est. monthly</span>
                </div>
                <p className="text-2xl font-bold text-[#0078C8]">
                  ${parseFloat(billing.estimated_monthly).toFixed(2)}
                </p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">{totalClients} total clients</p>
              </div>
            </div>

            {/* Trial limit indicator */}
            {billing.is_on_trial && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[13px] text-[rgba(0,10,30,0.75)]">Trial clients used</span>
                  <span className="text-[13px] font-medium text-[#000A1E]">
                    {totalClients} / {billing.trial_client_limit}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      totalClients >= billing.trial_client_limit ? 'bg-red-400' : 'bg-[#0078C8]'
                    }`}
                    style={{ width: `${Math.min((totalClients / billing.trial_client_limit) * 100, 100)}%` }}
                  />
                </div>
                {totalClients >= billing.trial_client_limit && (
                  <p className="text-[13px] text-red-500 mt-1">Limit reached — subscribe to add more clients</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscribe CTA */}
      {!isSubscribed && (
        <Card className="bg-[#0078C8]/5 border-[#0078C8]/10">
          <CardContent className="p-5">
            <h3 className="text-[13px] font-bold text-[#000A1E] mb-1">Ready to subscribe?</h3>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">
              Pay only for what you use — <strong>$5/client/month</strong> for Starter,{' '}
              <strong>$10/client/month</strong> for Premium. No client limits.
              {billing?.is_on_trial && ' Your remaining trial days carry over.'}
            </p>
            <Button onClick={handleSubscribe} disabled={subscribing}>
              {subscribing ? 'Redirecting to Stripe...' : 'Subscribe Now'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Plan comparison */}
      <div className="grid grid-cols-2 gap-5">
        {/* Starter */}
        <Card>
          <CardContent className="p-5 flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-[21px] font-bold text-[#000A1E] leading-[32px]">Starter</h3>
                <p className="text-2xl font-bold text-[#000A1E] mt-0.5">
                  $5 <span className="text-[13px] font-normal text-[rgba(0,10,30,0.75)]">/ client / month</span>
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            </div>
            <ul className="space-y-1.5 flex-1">
              {STARTER_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-[rgba(0,10,30,0.75)]">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-4 pt-4 border-t border-border">
              Set per client in Client Settings → Plan
            </p>
          </CardContent>
        </Card>

        {/* Premium */}
        <Card className="ring-2 ring-[#0078C8]">
          <CardContent className="p-5 flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[13px] font-bold text-[#0078C8] uppercase tracking-wider mb-1">Recommended</div>
                <h3 className="text-[21px] font-bold text-[#000A1E] leading-[32px]">Premium</h3>
                <p className="text-2xl font-bold text-[#000A1E] mt-0.5">
                  $10 <span className="text-[13px] font-normal text-[rgba(0,10,30,0.75)]">/ client / month</span>
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-[#0078C8] flex-shrink-0" />
            </div>
            <ul className="space-y-1.5 flex-1">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-[rgba(0,10,30,0.75)]">
                  <CheckCircle className="w-3.5 h-3.5 text-[#0078C8] flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-4 pt-4 border-t border-border">
              Set per client in Client Settings → Plan
            </p>
          </CardContent>
        </Card>
      </div>

      <p className="text-[13px] text-[rgba(0,10,30,0.75)] text-center">
        Mix plans freely — each client is billed at its own tier.
        Upgrade takes effect immediately (prorated). Downgrade takes effect next billing cycle.
        Archived clients are not billed.
      </p>
    </div>
  )
}
